/**
 * Tabs - a real tablist (docs/FRONTEND-PLAN.md section 2.1).
 *
 * Visual style is the segmented filter bar ReviewQueue hand-rolled: button
 * geometry, `ui.btnPrimary` for the selected tab, `ui.btnSecondary` for the
 * rest. What it adds is the APG keyboard contract - roving tabindex,
 * Left/Right arrows to move, Home/End to jump - which the hand-rolled bar
 * never had.
 *
 * This component renders the tablist only; the caller renders the panel:
 *
 *   <Tabs tabs={TABS} value={tab} onChange={setTab} aria-label="Filter by status" />
 *   <div role="tabpanel" id="filter-panel" aria-labelledby={`filter-${tab}`}>...</div>
 *
 * with `idPrefix="filter"` so the ids line up.
 */
import { useRef } from 'react';
import { CountBadge } from './ui';
import { ui } from '../lib/ui';

export default function Tabs({
  tabs = [],
  value,
  onChange,
  idPrefix = 'tab',
  'aria-label': ariaLabel,
}) {
  const nodesRef = useRef({});

  const selectedIndex = Math.max(
    0,
    tabs.findIndex(tab => tab.id === value)
  );

  function select(index) {
    if (tabs.length === 0) return;
    const next = tabs[((index % tabs.length) + tabs.length) % tabs.length];
    if (!next) return;
    onChange(next.id);
    const node = nodesRef.current[next.id];
    if (node) node.focus();
  }

  function handleKeyDown(event) {
    const targets = {
      ArrowLeft: selectedIndex - 1,
      ArrowRight: selectedIndex + 1,
      Home: 0,
      End: tabs.length - 1,
    };
    if (!(event.key in targets)) return;
    event.preventDefault();
    select(targets[event.key]);
  }

  return (
    <div className={ui.tabs} role="tablist" aria-label={ariaLabel} onKeyDown={handleKeyDown}>
      {tabs.map(tab => {
        const selected = tab.id === value;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`${idPrefix}-${tab.id}`}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            ref={node => {
              nodesRef.current[tab.id] = node;
            }}
            className={selected ? ui.btnPrimary : ui.btnSecondary}
            onClick={() => onChange(tab.id)}
          >
            {tab.label}
            <CountBadge count={tab.count} label={`${tab.count} in ${tab.label}`} />
          </button>
        );
      })}
    </div>
  );
}
